import {
  ACTIVE_ASSESSMENT_KEY,
  ASSESSMENT_HISTORY_KEY,
  importAssessmentHistoryLocal,
  loadAssessmentHistoryLocal,
} from "@/components/assessment/assessmentHistory";
import {
  loadParticipantIdentity,
  normalizeParticipantIdentity,
} from "@/components/assessment/participantIdentity";
import { DAILY_LOG_KEY } from "@/components/daily/dailyState";
import { COMPANION_CALENDAR_KEY } from "@/components/daily/companionCalendar";
import { ROUTINE_SETTINGS_KEY } from "@/components/daily/routineSettings";

export const CLOUD_ACCOUNT_CACHE_KEY = "forr:cloud-account:v1";
export const CLOUD_SYNC_STATE_KEY = "forr:cloud-sync-state:v1";
export const LOCAL_PROFILE_OWNER_KEY = "forr:local-profile-owner:v1";
export const DEVICE_ID_KEY = "forr:device-id:v1";
export const COMPANION_CONVERSATION_KEY = "forr:companion-conversation:v4";
export const PROFILE_DATA_SCHEMA_VERSION = 1;

const LEGACY_CONVERSATION_SESSION_KEY = "forr:companion-conversation:v3";
const IDENTITY_KEY = "forr:participant-identity:v1";

const PROFILE_CONTENT_KEYS = [
  IDENTITY_KEY,
  ASSESSMENT_HISTORY_KEY,
  ACTIVE_ASSESSMENT_KEY,
  "forr:adaptive-profile",
  "forr:daily-state-log:v1",
  "forr:daily-state-settings:v2",
  DAILY_LOG_KEY,
  COMPANION_CALENDAR_KEY,
  ROUTINE_SETTINGS_KEY,
  COMPANION_CONVERSATION_KEY,
];

const safeParse = (value, fallback) => {
  try {
    const parsed = JSON.parse(value);
    return parsed == null ? fallback : parsed;
  } catch (_error) {
    return fallback;
  }
};

const readJson = (key, fallback) => {
  try {
    return safeParse(localStorage.getItem(key), fallback);
  } catch (_error) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (_error) {
    return false;
  }
};

const removeKey = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch (_error) {
    // Continue clearing other profile keys.
  }
};

const recordTime = (record) => {
  for (const value of [record?.updatedAt, record?.completedAt, record?.createdAt]) {
    const time = new Date(value).getTime();
    if (Number.isFinite(time)) return time;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return 0;
};

const mergeRecords = (first = [], second = []) => {
  const values = new Map();
  [...(first || []), ...(second || [])].forEach((record) => {
    if (!record || typeof record !== "object") return;
    const id = String(
      record.id || `${record.createdAt || record.completedAt || ""}:${record.title || ""}`,
    );
    const current = values.get(id);
    if (!current || recordTime(record) >= recordTime(current)) values.set(id, record);
  });
  return [...values.values()].sort((a, b) => recordTime(a) - recordTime(b));
};

const assessmentKey = (record = {}) =>
  String(record.id || `${record.completedAt || "unknown"}:${record.profileKey || "profile"}`);

const mergeAssessments = (first = [], second = []) => {
  const values = new Map();
  [...(first || []), ...(second || [])].forEach((record) => {
    if (!record || typeof record !== "object") return;
    const id = assessmentKey(record);
    const current = values.get(id);
    if (!current || recordTime(record) >= recordTime(current)) values.set(id, record);
  });
  return [...values.values()].sort((a, b) => recordTime(a) - recordTime(b));
};

const hasIdentity = (identity) =>
  Boolean(identity?.preferredName || identity?.firstName || identity?.email);

const normalizedAccount = (account) => ({
  userId: account?.userId || null,
  email: String(account?.email || "").trim().toLowerCase(),
});

export function getDeviceId() {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const randomUUID = globalThis.crypto?.randomUUID;
    const value =
      typeof randomUUID === "function"
        ? randomUUID.call(globalThis.crypto)
        : `device-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(DEVICE_ID_KEY, value);
    return value;
  } catch (_error) {
    return `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function collectLocalProfileData() {
  const assessments = loadAssessmentHistoryLocal();
  const activeAssessmentId = (() => {
    try {
      return localStorage.getItem(ACTIVE_ASSESSMENT_KEY) || assessments.at(-1)?.id || null;
    } catch (_error) {
      return assessments.at(-1)?.id || null;
    }
  })();
  return {
    schemaVersion: PROFILE_DATA_SCHEMA_VERSION,
    identity: loadParticipantIdentity(),
    assessments,
    activeAssessmentId,
    dailyLog: readJson(DAILY_LOG_KEY, []),
    calendarEvents: readJson(COMPANION_CALENDAR_KEY, []),
    routineSettings: readJson(ROUTINE_SETTINGS_KEY, null),
    companionConversation: readJson(COMPANION_CONVERSATION_KEY, null),
    metadata: {
      deviceId: getDeviceId(),
      capturedAt: new Date().toISOString(),
    },
    updatedAt: new Date().toISOString(),
  };
}

export function mergeProfileData(cloudValue, localValue) {
  const cloud = cloudValue && typeof cloudValue === "object" ? cloudValue : {};
  const local = localValue && typeof localValue === "object" ? localValue : {};
  const assessments = mergeAssessments(cloud.assessments, local.assessments);
  const activeAssessmentId =
    local.activeAssessmentId ||
    cloud.activeAssessmentId ||
    assessments.at(-1)?.id ||
    null;
  return {
    ...cloud,
    ...local,
    schemaVersion: Math.max(
      Number(cloud.schemaVersion) || 1,
      Number(local.schemaVersion) || 1,
    ),
    identity: hasIdentity(local.identity) ? local.identity : cloud.identity || null,
    assessments,
    activeAssessmentId,
    dailyLog: mergeRecords(cloud.dailyLog, local.dailyLog),
    calendarEvents: mergeRecords(cloud.calendarEvents, local.calendarEvents),
    routineSettings: local.routineSettings || cloud.routineSettings || null,
    companionConversation:
      local.companionConversation || cloud.companionConversation || null,
    metadata: {
      ...(cloud.metadata || {}),
      ...(local.metadata || {}),
      deviceId: getDeviceId(),
      mergedAt: new Date().toISOString(),
    },
    updatedAt: new Date().toISOString(),
  };
}

export function clearLocalProfileContent() {
  PROFILE_CONTENT_KEYS.forEach((key) => removeKey(localStorage, key));
  if (typeof sessionStorage !== "undefined") {
    removeKey(sessionStorage, LEGACY_CONVERSATION_SESSION_KEY);
  }
}

export function hydrateLocalProfileData(value, { replace = false } = {}) {
  if (!value || typeof value !== "object") return collectLocalProfileData();
  if (replace) clearLocalProfileContent();

  const assessments = importAssessmentHistoryLocal(
    value.assessments || [],
    value.activeAssessmentId || null,
  );
  if (hasIdentity(value.identity)) {
    writeJson(IDENTITY_KEY, normalizeParticipantIdentity(value.identity));
  }
  writeJson(DAILY_LOG_KEY, Array.isArray(value.dailyLog) ? value.dailyLog : []);
  writeJson(
    COMPANION_CALENDAR_KEY,
    Array.isArray(value.calendarEvents) ? value.calendarEvents : [],
  );
  if (value.routineSettings) writeJson(ROUTINE_SETTINGS_KEY, value.routineSettings);
  if (value.companionConversation) {
    writeJson(COMPANION_CONVERSATION_KEY, value.companionConversation);
    if (typeof sessionStorage !== "undefined") {
      writeJsonToStorage(
        sessionStorage,
        LEGACY_CONVERSATION_SESSION_KEY,
        value.companionConversation,
      );
    }
  }
  if (value.activeAssessmentId) {
    try {
      localStorage.setItem(ACTIVE_ASSESSMENT_KEY, value.activeAssessmentId);
    } catch (_error) {
      // Assessment history remains available even if the active pointer cannot be written.
    }
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("apos-cloud-data-hydrated", {
        detail: {
          assessmentCount: assessments.length,
          activeAssessmentId: value.activeAssessmentId || null,
        },
      }),
    );
    window.dispatchEvent(new CustomEvent("adaptive-calendar-updated"));
  }
  return collectLocalProfileData();
}

function writeJsonToStorage(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch (_error) {
    return false;
  }
}

export function hasMeaningfulLocalProfileData(value = collectLocalProfileData()) {
  return Boolean(
    hasIdentity(value.identity) ||
      value.assessments?.length ||
      value.dailyLog?.length ||
      value.calendarEvents?.length ||
      value.companionConversation?.messages?.length,
  );
}

export function loadCachedCloudAccount() {
  return readJson(CLOUD_ACCOUNT_CACHE_KEY, null);
}

export function saveCachedCloudAccount(account) {
  if (!account) {
    removeKey(localStorage, CLOUD_ACCOUNT_CACHE_KEY);
    return null;
  }
  const value = {
    userId: account.userId || null,
    email: String(account.email || "").toLowerCase(),
    createdAt: account.createdAt || null,
    cachedAt: new Date().toISOString(),
  };
  writeJson(CLOUD_ACCOUNT_CACHE_KEY, value);
  return value;
}

export function loadLocalProfileOwner() {
  return readJson(LOCAL_PROFILE_OWNER_KEY, null);
}

export function saveLocalProfileOwner(account) {
  const value = normalizedAccount(account);
  if (!value.userId && !value.email) return null;
  writeJson(LOCAL_PROFILE_OWNER_KEY, value);
  return value;
}

export function localProfileBelongsToAccount(account) {
  const owner = loadLocalProfileOwner();
  const candidate = normalizedAccount(account);
  if (!owner) return false;
  if (owner.userId && candidate.userId) return owner.userId === candidate.userId;
  return Boolean(owner.email && candidate.email && owner.email === candidate.email);
}

export function claimUnownedLocalProfile(account) {
  if (loadLocalProfileOwner()) return false;
  if (!hasMeaningfulLocalProfileData()) return false;
  saveLocalProfileOwner(account);
  return true;
}

export function loadCloudSyncState() {
  return readJson(CLOUD_SYNC_STATE_KEY, { revision: 0, lastSyncedAt: null });
}

export function saveCloudSyncState(value) {
  const state = {
    revision: Number(value?.revision) || 0,
    lastSyncedAt: value?.lastSyncedAt || new Date().toISOString(),
    status: value?.status || "synced",
  };
  writeJson(CLOUD_SYNC_STATE_KEY, state);
  return state;
}

export function clearCloudAccountCache({ preserveOwner = true } = {}) {
  removeKey(localStorage, CLOUD_ACCOUNT_CACHE_KEY);
  removeKey(localStorage, CLOUD_SYNC_STATE_KEY);
  if (!preserveOwner) removeKey(localStorage, LOCAL_PROFILE_OWNER_KEY);
}

export function clearAllLocalProfileData({ preserveDeviceId = true } = {}) {
  clearLocalProfileContent();
  removeKey(localStorage, CLOUD_ACCOUNT_CACHE_KEY);
  removeKey(localStorage, CLOUD_SYNC_STATE_KEY);
  removeKey(localStorage, LOCAL_PROFILE_OWNER_KEY);
  if (!preserveDeviceId) removeKey(localStorage, DEVICE_ID_KEY);
}
